import React from 'react';

import type { TagEntity } from '@schibsted/frapi-schema';
import type { FilterMapping } from './helpers.js';

import {
  filterMappings,
  generateSearchQuery,
  getCategoryTitle,
  reduceFilters,
  formatTag,
  checkboxOrder,
  omraadeOrder,
  sortByPriority,
  generateTagQuery,
} from './helpers.js';
import { Dropdown } from './Dropdown.js';
import { FilterCloud } from './FilterCloud.js';

type SearchFiltersProps = {
  items: TagEntity[];
  activeFilters: TagEntity[];
  setFilter: (tag: TagEntity) => void;
  setOpen: (open: boolean) => void;
  section?: string;
  setActiveFilters: (filters: TagEntity[]) => void;
  searchTerm: string;
  presetTerm?: string;
};
export const SearchFilters: React.FC<SearchFiltersProps> = ({
  items: topTags,
  activeFilters,
  setFilter,
  setOpen,
  section,
  setActiveFilters,
  searchTerm,
  presetTerm,
}) => {
  const modifiedTags = reduceFilters(
    topTags.sort((a, b) => {
      const getOrder = (i: TagEntity) =>
        (section && filterMappings[section]?.[getCategoryTitle(i)]?.order) || 0;

      return getOrder(a) - getOrder(b);
    }),
  );

  const renderComponent = (
    category: string,
    instance: FilterMapping,
    filters: TagEntity[],
  ) => {
    switch (instance.component) {
      case 'Dropdown':
        return (
          <Dropdown
            setFilter={setFilter}
            filters={filters}
            activeFilters={activeFilters.filter(
              (f) => getCategoryTitle(f) === getCategoryTitle(filters[0]),
            )}
          />
        );

      case 'Checkbox':
        return (
          <FilterCloud
            activeFilters={activeFilters}
            filters={filters.sort(
              (a: TagEntity, b: TagEntity) =>
                checkboxOrder.indexOf(formatTag(a.title)) -
                checkboxOrder.indexOf(formatTag(b.title)),
            )}
            setFilter={setFilter}
            isCheckbox
          />
        );

      default:
        return (
          <FilterCloud
            activeFilters={activeFilters}
            filters={
              category === 'område'
                ? filters.sort(sortByPriority(omraadeOrder))
                : filters
            }
            setFilter={setFilter}
          />
        );
    }
  };

  const generateUrl = () => {
    let str = '';
    const term = searchTerm || presetTerm;
    if (term && section) {
      str = `/sok?phrase=${term}&size=36&section=${section}${generateSearchQuery(
        activeFilters,
        section,
      )}`;
    } else if (section) {
      str = `/samling/${section}/${generateTagQuery(activeFilters, section)}`;
    }

    return str;
  };

  return (
    <div className="filter-container">
      {Object.keys(modifiedTags).map((k, i) => {
        const sectionInstance = section ? filterMappings?.[section]?.[k] : null;

        if (sectionInstance) {
          return (
            <div key={k} className="tag-category-wrapper">
              <div className="tag-category-title">
                {sectionInstance?.title || k}
              </div>
              {renderComponent(k, sectionInstance, modifiedTags[k])}
            </div>
          );
        }

        return <React.Fragment key={i}></React.Fragment>;
      })}

      <div className="filter-action-buttons">
        <button className="button-hide-filters" onClick={() => setOpen(false)}>
          Skjul filter
        </button>
        {activeFilters.length > 0 && (
          <button
            className="button-hide-filters reset"
            onClick={() => setActiveFilters([])}
          >
            Nullstill
          </button>
        )}
      </div>
      {(searchTerm.length > 0 || activeFilters.length > 0) && (
        <a className="filtered-search-link" href={generateUrl()}>
          Søk
        </a>
      )}
    </div>
  );
};
